import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { referralSettingsSchema, insertReferralSettingsSchema, type ReferralSettings, type InsertReferralSettings } from '@/lib/schema';

// Legacy type for compatibility with existing UI
export interface ReferralSettingsData {
  cvUploadBonusAmount: number;
  clientReferralBonusAmount: number;
  noticePeriodDays: number;
  lastUpdated: Date;
  updatedBy: string;
}

// Get referral settings singleton document
export const getReferralSettings = async (): Promise<ReferralSettings | null> => {
  const settingsDoc = await getDoc(doc(db, 'referral_settings', 'global'));
  if (!settingsDoc.exists()) return null;
  
  const data = settingsDoc.data();
  return referralSettingsSchema.parse({
    id: 'global',
    ...data,
    lastUpdated: data.lastUpdated?.toDate() || new Date()
  }) as ReferralSettings;
};

// Get referral settings in the legacy format for UI compatibility
export const getReferralSettingsLegacy = async (): Promise<ReferralSettingsData | null> => {
  const settings = await getReferralSettings();
  if (!settings) return null;
  
  return {
    cvUploadBonusAmount: settings.cvUploadBonusAmount,
    clientReferralBonusAmount: settings.clientReferralBonusAmount,
    noticePeriodDays: settings.noticePeriodDays,
    lastUpdated: settings.lastUpdated,
    updatedBy: settings.updatedBy
  };
};

// Create or update referral settings
export const updateReferralSettings = async (
  data: InsertReferralSettings, 
  updatedBy: string
): Promise<ReferralSettings | null> => {
  const parsed = insertReferralSettingsSchema.parse(data);
  
  const settingsData = {
    ...parsed,
    lastUpdated: serverTimestamp(),
    updatedBy
  };
  
  await setDoc(doc(db, 'referral_settings', 'global'), settingsData);
  return getReferralSettings();
};

// Initialize default referral settings if they don't exist
export const initializeReferralSettings = async (adminUserId: string): Promise<ReferralSettings | null> => {
  const existing = await getReferralSettings();
  if (existing) return existing;
  
  const defaultSettings: InsertReferralSettings = {
    cvUploadBonusAmount: 500,
    clientReferralBonusAmount: 20000,
    noticePeriodDays: 90
  };
  
  return updateReferralSettings(defaultSettings, adminUserId);
};

// Helper functions to get individual setting values
export const getCvUploadBonusAmount = async (): Promise<number> => {
  const settings = await getReferralSettings();
  return settings?.cvUploadBonusAmount || 500;
};

export const getClientReferralBonusAmount = async (): Promise<number> => {
  const settings = await getReferralSettings();
  return settings?.clientReferralBonusAmount || 20000;
};

export const getNoticePeriodDays = async (): Promise<number> => {
  const settings = await getReferralSettings();
  return settings?.noticePeriodDays || 90;
};
